<template>
  <el-dropdown
    v-if="listNotifications.length"
    class="app-header-notification"
    popper-class="app-header-notification__popper"
    trigger="click"
    placement="bottom-end"
    @visible-change="onVisibilityChange"
    @command="handleCommand"
  >
    <div class="app-header-notification__icon">
      <el-badge :value="listNotifications.length" :max="99" class="item">
        <i class="ddi ddi-notification" title="Notifications" />
      </el-badge>
    </div>
    <template #dropdown>
      <el-dropdown-menu class="app-header-notification__dropdown">
        <el-dropdown-item class="app-header-notification__title no-hover">Notifications</el-dropdown-item>
        <el-dropdown-item
          v-for="item in listNotifications"
          :key="item.id"
          :command="item"
          :disabled="isLoading"
          class="app-header-notification__item"
        >
          <div class="app-header-notification__row">
            <div class="app-header-notification__avatar">
              <avatar :size="24" :person="item.creator" />
            </div>
            <div class="app-header-notification__markdown">
              <div>
                <span class="app-header-notification__action" v-html="item.action" />
                <priority-chicklet v-if="item.priority" :priority="item.priority" />
              </div>
              <div class="app-header-notification__description" v-html="item.description" />
            </div>
          </div>
        </el-dropdown-item>
        <div class="app-header-notification__clear-wrapper">
          <span @click="clearNotifications()">Clear all notifications</span>
        </div>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

import { mapState } from 'pinia'
/* Mixins */

/* Components */

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { INotificationsOutputNotification } from '@/api/types/response.types'

/* API */
import NotificationsAPI from '@/api/modules/notifications.api'
import { useProfileStore } from '@/modules/profile/profile.store'

export default defineComponent({
  name: 'AppHeaderNotification',

  data() {
    return {
      max: 4,
      isLoading: false,
      listNotifications: [] as INotificationsOutputNotification[],
    }
  },

  computed: {
    ...mapState(useProfileStore, ['notifications']),
  },

  watch: {
    notifications: {
      immediate: true,
      handler(val: INotificationsOutputNotification[]) {
        this.listNotifications = val
      },
    },
  },

  methods: {
    onVisibilityChange(visibility: boolean) {
      // only for mobile
      if (this.$screen.isMobile(this.$mq)) {
        if (visibility) {
          // @ts-ignore
          this.$bodyScroll.disable()
        } else {
          // @ts-ignore
          this.$bodyScroll.enable()
        }
      }
    },

    async handleCommand(item: any) {
      try {
        await NotificationsAPI.post_Read(item.id)
        await useProfileStore().notificationsFetch()
        this.goToNotificationUrl(item.url)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async clearNotifications() {
      try {
        await NotificationsAPI.post_Clear()
        await useProfileStore().notificationsFetch()
        if (this.$screen.isMobile(this.$mq)) {
          /* Re-enable scrolling that was disabled for mobile upon showing dropdown */
          // @ts-ignore
          this.$bodyScroll.enable()
        }
      } catch (error) {
        this.$error.emit(error)
      }
    },

    goToNotificationUrl(url: string): void {
      /* Notification urls should be the "beta-redirect" urls */
      const relativeMarker = 'project-type'

      /* If this is another kind of notification url, just go to it... */
      if (url.indexOf(relativeMarker) === -1) {
        RouteAssistant.goToUrl(url)
        return
      }

      const relativeUrl = url.substring(
        url.indexOf(relativeMarker) + relativeMarker.length,
      )

      const numbers = relativeUrl.match(/\d+/g).map(Number)
      let projectTypeID = 0
      let projectID = 0
      let itemID = 0
      let eventID = null
      const accountID = this.$route.params.accountId

      if (numbers.length === 3) {
        // e.g. https://2.donedone.com/app/1/beta-redirect/project-type/2/items/4468/conversation/28291
        projectTypeID = numbers[0]
        projectID = numbers[1]
        itemID = numbers[2]
      } else if (numbers.length === 4) {
        // e.g. https://2.donedone.com/app/1/beta-redirect/project-type/2/items/4468/conversation/28291?item_event_id=97599
        projectTypeID = numbers[0]
        projectID = numbers[1]
        itemID = numbers[2]
        eventID = numbers[3]
      } else {
        RouteAssistant.goToUrl(url)
        return
      }

      const isTask = projectTypeID === 1

      // project
      if (isTask) {
        RouteAssistant.goToTask(
          this.$router,
          accountID,
          projectID,
          itemID,
          this.$permissions,
          null,
          null,
          null,
          null,
          eventID,
        )
      } else {
        RouteAssistant.goToConversation(
          this.$router,
          accountID,
          projectID,
          itemID,
          null,
          null,
          null,
          null,
          eventID,
        )
      }
    },
  },
})
</script>

<style lang="scss">
.el-dropdown-menu.app-header-notification__dropdown {
  background-color: var(--color-background-header);
}

/* The dropdown's popper, which is what gets positioned; the extra class outranks the popper rules in _dropdown.scss */
.el-dropdown__popper.el-popper.app-header-notification__popper {
  background-color: var(--color-background-header);
  /* Element UI's 17px, less the 12px Element Plus already leaves under the trigger */
  margin-top: 5px;

  /* We need to add important statements here because Element adds inline styles */
  @include media-breakpoint-down(sm) {
    margin-top: 0;
    position: fixed !important;
    top: $navbar-mobile-height !important;
    bottom: 0 !important;
    left: 0 !important;
    width: 100% !important;
    min-width: 320px;
    max-width: 768px;
    overflow-x: hidden;
    outline: none;
  }

  @include media-breakpoint-up(md) {
    max-width: 342px;
    max-height: 500px;
    overflow-y: auto;
    transform: translate(10px, -15px);
  }

  .el-popper__arrow {
    display: none;
  }
}

.app-header-notification {
  /* Element Plus sets 1; the header's line height makes the trigger as tall as the header, as with Element UI, which
     the badge offset and the dropdown's position are measured from */
  line-height: inherit;

  &__title {
    margin-bottom: 6px;
    @include lettering--h5;
    color: var(--color-text-primary) !important;
  }

  &__icon {
    display: flex;
    outline: none;

    @include clickable-gray-color-states;

    .el-badge {
      top: 2px;
      .el-badge__content.is-fixed {
        top: -5px;
        right: -2px;
        padding: 0 4px;
        font-size: 10px;
        font-weight: 500;
        line-height: 14px;
        background-color: $color--red;
        border-width: 2px;
        transform: none;

        @include media-breakpoint-up(md) {
          top: 13px;
        }
      }
    }

    .ddi-notification {
      position: relative;
      font-size: $dd2-size--app-header-icon;
      top: -2px;
    }
  }

  &__item {
    padding-right: 20px !important;
  }

  &__row {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: baseline;
    padding: 4px 0 10px 0;
  }

  &__markdown {
    width: 100%;
    padding-left: 4px;
    margin-left: 5px;

    > div:first-child {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__action {
    width: 100%;

    p {
      @include lettering--p-sm;
    }
  }

  &__description {
    margin-top: 4px;

    > p {
      @include lettering--p-xs;
      color: $color--gray-60 !important;
    }
  }

  &__clear-wrapper {
    display: flex;
    justify-content: center;
    padding: 10px 0 2px 0;
    @include lettering--p-sm;
    @include lettering--normal-link;

    > span {
      cursor: pointer;
    }
  }
}
</style>
